var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { focusable } from 'tabbable';
import { KeyAttributeValue, mergeProps, useClasses, useLayoutProps, useProps, } from '@hawkins/helpers';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { FloatingUI } from '../floatingUi';
import { useClickOutside, useCombineRefs, useEffectPostInitialRender, useId, useScrollLock, } from '../hooks';
import { useMenuStyles } from './menu.styles';
import { MenuContext, MenuTriggerContext } from './menuContext';
var displayName = 'Menu';
/**
 * A menu is a list of actions or selections.
 */
export var Menu = React.forwardRef(function (_a, refProp) {
    var _b;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _c = useProps(displayName, props), anchorEl = _c.anchorEl, _d = _c.caretDirectionClosed, caretDirectionClosed = _d === void 0 ? 'down' : _d, classesProp = _c.classes, className = _c.className, _e = _c.closeOnClickOutside, closeOnClickOutside = _e === void 0 ? true : _e, _f = _c.closeOnEsc, closeOnEsc = _f === void 0 ? true : _f, _g = _c.closeOnSelect, closeOnSelect = _g === void 0 ? true : _g, _h = _c.component, Component = _h === void 0 ? 'ul' : _h, _j = _c.defaultOpen, defaultOpen = _j === void 0 ? false : _j, _k = _c.disableAutoFocusOnClose, disableAutoFocusOnClose = _k === void 0 ? false : _k, _l = _c.disableAutoFocusOnOpen, disableAutoFocusOnOpen = _l === void 0 ? true : _l, _m = _c.disablePortal, disablePortal = _m === void 0 ? false : _m, _o = _c.disableScrollLock, disableScrollLock = _o === void 0 ? false : _o, _p = _c.disabled, disabled = _p === void 0 ? false : _p, _q = _c.floatingUiProps, floatingUiProps = _q === void 0 ? {} : _q, _r = _c.fullWidth, fullWidth = _r === void 0 ? false : _r, defaultId = _c.id, layout = _c.layout, marginThreshold = _c.marginThreshold, onToggle = _c.onToggle, openProp = _c.open, _s = _c.position, position = _s === void 0 ? 'bottom-start' : _s, _t = _c.showCaret, showCaret = _t === void 0 ? false : _t, styleProp = _c.style, trigger = _c.trigger, _u = _c.triggerContainerBlock, triggerContainerBlock = _u === void 0 ? false : _u, triggerContainerProps = _c.triggerContainerProps, useScrollLockParams = _c.useScrollLockParams, restProps = __rest(_c, ["anchorEl", "caretDirectionClosed", "classes", "className", "closeOnClickOutside", "closeOnEsc", "closeOnSelect", "component", "defaultOpen", "disableAutoFocusOnClose", "disableAutoFocusOnOpen", "disablePortal", "disableScrollLock", "disabled", "floatingUiProps", "fullWidth", "id", "layout", "marginThreshold", "onToggle", "open", "position", "showCaret", "style", "trigger", "triggerContainerBlock", "triggerContainerProps", "useScrollLockParams"]);
    var id = useId(displayName, { defaultId: defaultId });
    var hasAnchorEl = !!anchorEl;
    var hasTrigger = !!trigger;
    var isOpenControlled = openProp !== undefined;
    var isInFloatingUI = hasTrigger || isOpenControlled;
    // Refs
    var internalRef = React.useRef(null);
    var internalTriggerContainerRef = React.useRef(null);
    // State
    var _v = __read(React.useState(defaultOpen), 2), internalOpen = _v[0], setInternalOpen = _v[1];
    var _w = __read(React.useState(null), 2), internalRefState = _w[0], setInternalRefState = _w[1];
    var _x = __read(React.useState(null), 2), triggerContainerRefState = _x[0], setTriggerContainerRefState = _x[1];
    var _y = __read(React.useState(''), 2), triggerId = _y[0], setTriggerId = _y[1];
    var isInternalRefSet = !!internalRefState;
    var isTriggerContainerRefSet = !!triggerContainerRefState;
    var open = isOpenControlled ? openProp : internalOpen;
    useScrollLock(__assign({ lockScroll: open && !disableScrollLock }, useScrollLockParams));
    // Auto-focus when the menu opens and closes.
    useEffectPostInitialRender(function () {
        var _a;
        if (!isInFloatingUI)
            return;
        if (open) {
            if (disableAutoFocusOnOpen ||
                !internalRef.current ||
                // This effect may be triggered multiple times in quick succession based on its dependency array,
                // so don't re-focus on the menu if focus is already on the first menu item.
                internalRef.current.contains(document.activeElement)) {
                return;
            }
            // Auto-focus on the menu.
            internalRef.current.focus();
        }
        else {
            if (disableAutoFocusOnClose || !internalTriggerContainerRef.current)
                return;
            // Auto-focus on the menu trigger.
            (_a = focusable(internalTriggerContainerRef.current)[0]) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [
        isInFloatingUI,
        // Need to trigger based on `internalRefState` since `internalRef.current`
        // might not be set in time due to being inside a <FloatingUI>.
        isInternalRefSet,
        open,
    ]);
    var closeMenu = React.useCallback(function (ev) {
        if (!isInFloatingUI || disabled)
            return;
        var newOpen = false;
        if (!isOpenControlled)
            setInternalOpen(newOpen);
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(newOpen, ev);
    }, [disabled, isInFloatingUI, isOpenControlled, onToggle]);
    var onOutsideClick = React.useCallback(function (ev) {
        var _a, _b;
        if (!closeOnClickOutside)
            return;
        if (!open)
            return;
        if (ev.target instanceof Element) {
            // If the menu trigger was clicked, do nothing.
            if ((_a = internalTriggerContainerRef.current) === null || _a === void 0 ? void 0 : _a.contains(ev.target))
                return;
            // If a nested menu item was clicked, do nothing
            // (nested menus are rendered inside portals, so clicking them will trigger the `useClickOutside` hook).
            var parentMenu = ev.target.closest('[role="menu"]');
            var labelledBy = parentMenu === null || parentMenu === void 0 ? void 0 : parentMenu.getAttribute('aria-labelledby');
            while (labelledBy) {
                // This menu might be labelled by its menu trigger,
                // so don't set `parentMenu` to `potentialParentMenu` if we've reached that point.
                var potentialParentMenu = ((_b = document.querySelector("#".concat(labelledBy))) === null || _b === void 0 ? void 0 : _b.closest('[role="menu"]')) || null;
                if (!potentialParentMenu)
                    break;
                parentMenu = potentialParentMenu;
                labelledBy = parentMenu.getAttribute('aria-labelledby');
            }
            if (parentMenu === internalRef.current)
                return;
        }
        closeMenu(ev);
    }, [closeMenu, closeOnClickOutside, open]);
    var clickOutsideRef = useClickOutside({ onOutsideClick: onOutsideClick });
    var ref = useCombineRefs([clickOutsideRef, internalRef, refProp, setInternalRefState]);
    var combinedTriggerContainerRef = useCombineRefs([
        internalTriggerContainerRef,
        setTriggerContainerRefState,
    ]);
    // Context
    var menuContext = React.useMemo(function () { return ({ closeMenu: closeMenu, closeOnSelect: closeOnSelect, open: open }); }, [closeMenu, closeOnSelect, open]);
    var menuTriggerContext = React.useMemo(function () { return ({ caretDirectionClosed: caretDirectionClosed, menuId: id, open: open, setTriggerId: setTriggerId, showCaret: showCaret }); }, [caretDirectionClosed, id, open, showCaret]);
    // Callbacks
    var onBlur = React.useCallback(function (ev) {
        // Close the menu when focus goes from the menu to an element outside the menu.
        if (internalRef.current &&
            ev.target === internalRef.current &&
            ev.relatedTarget instanceof Element &&
            !internalRef.current.contains(ev.relatedTarget)) {
            closeMenu(ev);
        }
    }, [closeMenu]);
    var onFocus = React.useCallback(function (ev) {
        var _a;
        // Focus on the menu's first focusable descendent
        // when focus goes to the menu from an element outside the menu.
        if (internalRef.current &&
            ev.target === internalRef.current &&
            (ev.relatedTarget instanceof Element || ev.relatedTarget === null) &&
            !internalRef.current.contains(ev.relatedTarget)) {
            (_a = focusable(internalRef.current)[0]) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, []);
    var onKeyDown = React.useCallback(function (ev) {
        var _a, _b;
        if (!internalRef.current)
            return;
        var focusableEls = focusable(internalRef.current);
        var targetIdx = focusableEls.findIndex(function (el) { return el === ev.target; });
        // Only handle keyboard interactions on the menu itself or one of its descendants
        // (i.e., not on a nested menu inside a <FloatingUI>).
        if (targetIdx === -1 && ev.target !== internalRef.current)
            return;
        if (ev.key === KeyAttributeValue.ARROW_DOWN) {
            // Prevent the page from scrolling.
            ev.preventDefault();
            (_a = focusableEls[targetIdx >= focusableEls.length - 1 ? 0 : targetIdx + 1]) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else if (ev.key === KeyAttributeValue.ARROW_UP) {
            // Prevent the page from scrolling.
            ev.preventDefault();
            (_b = focusableEls[targetIdx <= 0 ? focusableEls.length - 1 : targetIdx - 1]) === null || _b === void 0 ? void 0 : _b.focus();
        }
        else if (ev.key === KeyAttributeValue.ESCAPE) {
            if (closeOnEsc)
                closeMenu(ev);
        }
        else if (ev.key === KeyAttributeValue.TAB) {
            if (isInFloatingUI) {
                // Allowing the default tabbing behavior to take place gets buggy due to
                // how this component manages focus, so just let focus end up on the menu trigger after the menu is closed.
                //
                // TODO: Improve the way focus is managed in this component?
                ev.preventDefault();
                closeMenu(ev);
            }
            else {
                // Do NOT call `ev.preventDefault()`
                // so that tabbing behavior will occur as normal once focus has moved to the menu.
                internalRef.current.focus();
            }
        }
    }, [closeMenu, closeOnEsc, isInFloatingUI]);
    var onTriggerClick = React.useCallback(function (ev) {
        if (disabled)
            return;
        if (open) {
            closeMenu(ev);
        }
        else {
            var newOpen = true;
            if (!isOpenControlled)
                setInternalOpen(newOpen);
            onToggle === null || onToggle === void 0 ? void 0 : onToggle(newOpen, ev);
        }
    }, [closeMenu, disabled, isOpenControlled, onToggle, open]);
    // Allow the user to move focus into the menu if the menu is already open (e.g., if `defaultOpen=true`).
    var onTriggerKeyDown = React.useCallback(function (ev) {
        if (!open || !internalRef.current)
            return;
        if (ev.key === KeyAttributeValue.ARROW_DOWN) {
            // Prevent the page from scrolling.
            ev.preventDefault();
            (focusable(internalRef.current)[0] || internalRef.current).focus();
        }
        else if (ev.key === KeyAttributeValue.ARROW_UP) {
            // Prevent the page from scrolling.
            ev.preventDefault();
            var focusableEls = focusable(internalRef.current);
            (focusableEls[focusableEls.length - 1] || internalRef.current).focus();
        }
        else if (ev.key === KeyAttributeValue.ESCAPE) {
            if (closeOnEsc)
                closeMenu(ev);
        }
    }, [closeMenu, closeOnEsc, open]);
    // Styles
    var baseClasses = useMenuStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, HawkinsGlobalClass.FOCUSABLE, className, classes.root);
    var triggerContainerClassName = clsx(classes.triggerContainer, (_b = {},
        _b[classes.triggerContainerBlock] = triggerContainerBlock,
        _b));
    // Content
    var menuNode = (_jsx(MenuContext.Provider, __assign({ value: menuContext }, { children: _jsx(Component, __assign({ "aria-labelledby": triggerId || undefined, className: rootClassName, id: id, ref: ref, role: "menu", style: style, 
            // Make the menu focusable to make handling focus easier
            // (by centralizing the logic within the menu's `onFocus` and `onBlur` handlers).
            //
            // Also make the menu tabbable to keep it keyboard-accessible when
            // it has no focusable descendants (i.e., the menu itself needs a focus ring)
            // or is not inside a <FloatingUI> (i.e., the menu cannot be opened or closed).
            tabIndex: 0 }, mergeProps(restProps, { onBlur: onBlur, onFocus: onFocus, onKeyDown: onKeyDown }), { children: children })) })));
    var floatingUINode = (_jsx(FloatingUI, __assign({ anchor: anchorEl || internalTriggerContainerRef.current, className: classes.floatingUI, open: open && (hasAnchorEl || isTriggerContainerRefSet), placement: position, styled: false, widthVariant: fullWidth ? 'full' : 'auto', disablePortal: disablePortal }, floatingUiProps, { children: menuNode })));
    if (!trigger)
        return isInFloatingUI ? floatingUINode : menuNode;
    return (_jsxs(_Fragment, { children: [_jsx(MenuTriggerContext.Provider, __assign({ value: menuTriggerContext }, { children: _jsx("div", __assign({ className: triggerContainerClassName, ref: combinedTriggerContainerRef }, triggerContainerProps, { children: React.cloneElement(trigger, {
                        onClick: function (ev) {
                            var _a, _b;
                            (_b = (_a = trigger.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, ev);
                            onTriggerClick(ev);
                        },
                        onKeyDown: function (ev) {
                            var _a, _b;
                            (_b = (_a = trigger.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, ev);
                            onTriggerKeyDown(ev);
                        },
                    }) })) })), floatingUINode] }));
});
Menu.displayName = displayName;
